import { DecisionHistoryRecordV2 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { NodeBE } from "src/api/flowTypes";
import { useReviewCases } from "src/api/queries";
import { DecisionEnvironment } from "src/api/types";
import { InspectDataBase } from "src/dataTable/DetailedView/tabs/InspectData";
import { useDecisionsOverviewQueryParams } from "src/decisionsOverview/DecisionsOverview";
import { Button } from "src/design-system/Button";
import { Pill } from "src/design-system/Pill";
import { ShortDateFromNow } from "src/flowReview/ReviewLogEntry";
import { UserTip } from "src/flowReview/ReviewStatusPills";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { ReviewCaseStatus } from "src/manualReview/ReviewCaseStatus";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToReviewCase } from "src/router/urls";
import { formatDate } from "src/utils/datetime";

export const PendingManualReviewOutput: React.FC<{
  node: NodeBE;
  decision: DecisionHistoryRecordV2;
}> = ({ decision, node }) => {
  const { workspace, orgId } = useWorkspaceContext();
  const { decisionId: urlDecisionId, entityId: urlEntityId } =
    useDecisionsOverviewQueryParams();
  const { data, isLoading } = useReviewCases(
    workspace.base_url!,
    decision.flow.slug,
    {
      entity_or_decision_id: decision.id,
      environment: decision.is_sandbox ? "sandbox" : "prod",
    },
  );

  // there can be only one case that needs review for a decision at any given time
  const reviewCase = data?.pages
    .at(0)
    ?.cases.find((c) => c.status !== "completed");

  if (isLoading) {
    return (
      <div className="px-4">
        <InspectDataBase
          accessedFields={[]}
          rowData={undefined}
          selectedRowIndex={null}
          withAccessedFields={false}
          dividers
          isFetching
          isHistorical
        />
      </div>
    );
  }

  const rows: [string, React.ReactNode][] = [
    ["Request time", formatDate(decision.start_time)],
    ["Needs review since", <ShortDateFromNow date={decision.end_time} />],
    [
      "Assigned to",
      reviewCase?.assignee ? (
        <UserTip orgId={orgId} userId={reviewCase.assignee} />
      ) : (
        "-"
      ),
    ],
    [
      "Version",
      <Pill size="sm" variant="gray">
        <Pill.Text>{decision.flow.version_name}</Pill.Text>
      </Pill>,
    ],
    ["Node", node.name],
    ["Status", reviewCase && <ReviewCaseStatus status={reviewCase?.status} />],
  ];

  return (
    <div className="flex flex-col gap-y-4 px-4">
      <div>
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex border-gray-100 border-b font-inter-medium-12px text-gray-800"
            data-loc={`pending-manual-review-output-${label}`}
          >
            <div className="w-36 shrink-0 p-1.5">{label}</div>
            <div className="flex flex-grow items-center gap-x-1 p-1.5">
              {value}
            </div>
          </div>
        ))}
      </div>
      {reviewCase && (
        <div>
          <Button
            dataLoc="view-review-case-button"
            size="sm"
            onClick={() => {
              tracker.emit(
                trackingEvents.gotoReviewCase({
                  query_param_decision_id: urlDecisionId,
                  query_param_entity_id: urlEntityId,
                  decision_id: decision.id,
                  review_case_id: reviewCase.id,
                  organization_id: orgId,
                  env: decision.is_sandbox
                    ? DecisionEnvironment.SANDBOX
                    : DecisionEnvironment.LIVE,
                }),
              );
            }}
          >
            <a
              href={
                getBaseUrl() +
                getUrlToReviewCase(
                  orgId,
                  workspace.id,
                  decision.flow.id,
                  reviewCase.id,
                )
              }
              rel="noreferrer"
              target="_blank"
            >
              View review case
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};
